import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Plus, Trash2 } from 'lucide-react';
import { formatUsd } from '@/lib/estimatesData';

export const PaymentScheduleEditor = ({
  estimate,
  totals,
  onAdd,
  onUpdate,
  onRemove,
}) => {
  const { t } = useTranslation();
  const rows = Array.isArray(estimate?.payment_schedule)
    ? estimate.payment_schedule
    : [];
  const totalPercent = rows.reduce(
    (s, r) => s + (Number(r?.percent) || 0),
    0
  );
  const valid = Math.abs(totalPercent - 100) < 0.001;

  return (
    <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
            {t('estimates.payment_schedule.title')}
          </div>
          <Badge
            variant="outline"
            className={
              valid
                ? 'uppercase text-[10px] tracking-[0.16em] bg-[hsl(var(--success))]/12 text-[hsl(var(--success))] border-[hsl(var(--success))]/30'
                : 'uppercase text-[10px] tracking-[0.16em] bg-[hsl(var(--warning))]/12 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30'
            }
            data-testid="payment-schedule-percent"
          >
            {t('estimates.payment_schedule.allocated', {
              percent: totalPercent.toFixed(0),
            })}
          </Badge>
        </div>

        <div className="mt-3 space-y-2" data-testid="payment-schedule-rows">
          {rows.map((row) => {
            const amount =
              ((Number(row?.percent) || 0) / 100) * (totals?.total || 0);
            return (
              <div
                key={row.id}
                className="grid grid-cols-12 items-center gap-2 rounded-md border border-border bg-[hsl(var(--background))]/40 px-2.5 py-2"
                data-testid={`payment-row-${row.id}`}
              >
                <Input
                  value={row.label || ''}
                  onChange={(e) =>
                    onUpdate(row.id, { label: e.target.value })
                  }
                  placeholder={t('estimates.payment_schedule.label_placeholder')}
                  className="col-span-12 sm:col-span-4 h-9 bg-[hsl(var(--muted))] border-border text-foreground"
                  data-testid={`payment-row-label-${row.id}`}
                />
                <div className="col-span-4 sm:col-span-2 relative">
                  <Input
                    type="number"
                    inputMode="decimal"
                    min="0"
                    max="100"
                    step="1"
                    value={row.percent ?? 0}
                    onChange={(e) =>
                      onUpdate(row.id, { percent: Number(e.target.value) || 0 })
                    }
                    className="h-9 pr-6 bg-[hsl(var(--muted))] border-border text-foreground"
                    data-testid={`payment-row-percent-${row.id}`}
                  />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                    %
                  </span>
                </div>
                <Input
                  value={row.due || ''}
                  onChange={(e) => onUpdate(row.id, { due: e.target.value })}
                  placeholder={t('estimates.payment_schedule.due_placeholder')}
                  className="col-span-8 sm:col-span-3 h-9 bg-[hsl(var(--muted))] border-border text-foreground"
                  data-testid={`payment-row-due-${row.id}`}
                />
                <div className="col-span-9 sm:col-span-2 sm:text-right text-sm font-semibold text-foreground tabular-nums">
                  {formatUsd(amount)}
                </div>
                <div className="col-span-3 sm:col-span-1 text-right">
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => onRemove(row.id)}
                    className="text-[hsl(var(--destructive))] hover:bg-[hsl(var(--destructive))]/10 h-7 w-7"
                    aria-label={t('estimates.payment_schedule.remove_milestone')}
                    data-testid={`payment-row-remove-${row.id}`}
                  >
                    <Trash2 size={12} />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-3 flex items-center justify-end">
          <Button
            size="sm"
            variant="outline"
            onClick={onAdd}
            className="border-[hsl(var(--primary))]/30 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10 uppercase tracking-[0.16em] text-xs"
            data-testid="payment-row-add"
          >
            <Plus size={14} className="mr-1.5" />
            {t('estimates.payment_schedule.add_milestone')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default PaymentScheduleEditor;
